import React, { useState, useContext } from "react";
import { Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { getToken } from "../../../../api/Account";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { EstadoChip, useAviso } from "../../../../components/base";
import { formatearFecha, formatearMonto } from "../../../../helpers/formato";
import {
  deleteAdelantoByIdAsync,
  getAdelantosByEmployeeAsync,
} from "../../../../api/Employees";
import AddAdelanto from "../adelantos/AddAdelanto";
import UpdateAdelanto from "../adelantos/UpdateAdelanto";
import HistorialTab from "./HistorialTab";
import {
  celdaFecha,
  celdaMonto,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

const FILAS_POR_PAGINA = 10;

// "Cancelado" en el sistema = ya se descontó en una nómina (o en la liquidación).
const EstadoAdelanto = ({ descontado }) =>
  descontado ? (
    <EstadoChip estado="neutro" label="Descontado" />
  ) : (
    <EstadoChip estado="aviso" label="Pendiente" />
  );

const Adelantos = ({ id }) => {
  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const [selectedAdelanto, setSelectedAdelanto] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const {
    datos: histoyAdelantos,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getAdelantosByEmployeeAsync(token, id));

  const deleteAdelanto = async (adelantoId) => {
    const ok = await escribir(
      () => deleteAdelantoByIdAsync(token, adelantoId),
      "No se pudo eliminar el adelanto"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Adelanto eliminado");
  };

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar adelanto",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminará el adelanto #${row.id} de ${formatearMonto(
              row.montoAdelanto
            )} del ${formatearFecha(row.fechaCreacion)}.`}
          </Typography>
          {row.isCancelled ? (
            <Typography component="p" sx={{ mb: 1 }}>
              Este adelanto ya se descontó en una nómina.
            </Typography>
          ) : null}
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar adelanto",
      peligro: true,
      onConfirmar: () => deleteAdelanto(row.id),
    });

  // Agregar es el Add... de EmployeesController/NominasController (NOMINA CREAR).
  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  const puedeEditar = tienePermiso(access, "NOMINA UPDATE");
  // La pantalla pedía NOMINA DELETE y DeleteAdelanto exige NOMINA UPDATE en el servidor: se piden los dos.
  const puedeEliminar =
    tienePermiso(access, "NOMINA DELETE") && tienePermiso(access, "NOMINA UPDATE");

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "sm",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "fecha",
      etiqueta: "Fecha de solicitud",
      sx: celdaFecha,
      render: (row) => formatearFecha(row.fechaCreacion),
    },
    {
      id: "monto",
      etiqueta: "Monto",
      alinear: "right",
      sx: celdaMonto,
      render: (row) => (
        <Typography variant="monto" component="span">
          {formatearMonto(row.montoAdelanto)}
        </Typography>
      ),
    },
    {
      id: "estado",
      etiqueta: "Estado",
      desde: "sm",
      render: (row) => <EstadoAdelanto descontado={row.isCancelled} />,
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Adelantos"
        descripcion="Adelantos de salario que se descuentan de la nómina."
        etiquetaTabla="Historial de adelantos"
        agregar={
          puedeAgregar ? { etiqueta: "Agregar adelanto", onClick: () => setShowModal(true) } : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando adelantos…"
        filas={histoyAdelantos}
        columnas={columnas}
        detalleMovil={(row) => <EstadoAdelanto descontado={row.isCancelled} />}
        filasPorPagina={FILAS_POR_PAGINA}
        etiquetaFila={(row) => `adelanto #${row.id}`}
        vacio={{
          titulo: "Sin adelantos",
          descripcion: "Aquí aparecen los adelantos de salario del empleado.",
        }}
        acciones={(row) => [
          {
            id: "editar",
            etiqueta: "Editar",
            icono: <EditOutlinedIcon />,
            visible: puedeEditar,
            onClick: () => {
              setSelectedAdelanto(row);
              setShowEditModal(true);
            },
          },
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            visible: puedeEliminar,
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      {showModal ? (
        <AddAdelanto
          id={id}
          setShowModal={setShowModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {showEditModal ? (
        <UpdateAdelanto
          selected={selectedAdelanto}
          setShowModal={setShowEditModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {dialogo}
    </div>
  );
};

export default Adelantos;
